import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TableSortLabel,
  TablePagination,
  TextField,
  Modal,
  Box
} from "@mui/material";
import DocumentInfo from "../doc-view/DocumentInfo"; 
import { getAuth } from "firebase/auth";
import callApi from "../common/APIClient"
import { useNavigate } from "react-router-dom";



// Status Colors
const getStatusChip = (status) => {
  switch (status) {
    case "Uploaded":
      return <Chip label="Uploaded" style={{ backgroundColor: "#00c853", color: "#fff" }} />;
    case "In Progress":
      return <Chip label="In Progress" style={{ backgroundColor: "#ffab00", color: "#fff" }} />;
    case "Failed":
      return <Chip label="Failed" style={{ backgroundColor: "#d50000", color: "#fff" }} />;
    default:
      return <Chip label="Unknown" />;
  }
};

// Sorting helper functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

const DocumentStatusTable = () => {
  const [documents, setDocuments] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterText, setFilterText] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [groupedEntities,setGroupEntities] = useState({});;
  const navigate = useNavigate();
  
  // Fetch documents from the API
  const getDocuments = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await callApi({
        url: "/documents",
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      setDocuments(response.documents);
      console.log("Server Response:", response);
    } catch (error) {
      console.error("Processing Document error:", error);
    }
  };

  useEffect(() => {
    getDocuments();
  }, []);

  // Function to open a new window and store the selected doc in localStorage
  const handleRowClick = (doc) => {
    navigate(`/doc-center/doc-view/${doc.doc_id}`, {
      state: { doc },
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDoc(null);
    setGroupEntities(null);
  };

  // Handle sorting by header
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

 // Filter documents based on the file_name
  const filteredDocs = documents.filter((doc) =>
    doc.file_name.toLowerCase().includes(filterText.toLowerCase())
  );

  // Sort the filtered documents and then paginate
  const sortedDocs = stableSort(filteredDocs, getComparator(order, orderBy));
  const displayedDocs = sortedDocs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const groupEntities = (entities) => {
    if (!Array.isArray(entities)) {
      console.log(typeof entities);
      console.error("Expected an array but received:", entities);
      return {};
    }
    return entities.reduce((acc, entity) => {
      const key = entity.entity_group;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(entity.word.replace(/##/g, ""));
      return acc;
    }, {});
  };

  return (
    <Paper sx={{ backgroundColor: "#0d1b2a", color: "#00f5d4" }}>
      {/* Filtering */}
      <TextField
        label="Filter by Document Name"
        variant="outlined"
        size="small"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        style={{ margin: 16 }}
      />
      <TableContainer component={Paper} sx={{ backgroundColor: "#0d1b2a", color: "#00f5d4" }}>
        <Table>
          <TableHead sx={{ color: "#38bdf8"}}>
            <TableRow sx={{ backgroundColor: "rgba(13, 27, 42, 0.9)", color: "#00f5d4", fontWeight: "bold" }}>
              <TableCell sortDirection={orderBy === "file_name" ? order : false} sx={{ color: "#00f5d4"}}>
                <TableSortLabel
                  active={orderBy === "file_name"}
                  direction={orderBy === "file_name" ? order : "asc"}
                  onClick={(e) => handleRequestSort(e, "file_name")}
                >
                  Document Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false} sx={{ color: "#00f5d4"}}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={(e) => handleRequestSort(e, "status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "doc_id" ? order : false} sx={{ color: "#00f5d4"}}>
                <TableSortLabel
                  active={orderBy === "doc_id"}
                  direction={orderBy === "doc_id" ? order : "asc"}
                  onClick={(e) => handleRequestSort(e, "doc_id")}
                >
                  Doc Id
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "file_url" ? order : false} sx={{ color: "#00f5d4"}}>
                <TableSortLabel
                  active={orderBy === "file_url"}
                  direction={orderBy === "file_url" ? order : "asc"}
                  onClick={(e) => handleRequestSort(e, "file_url")}
                >
                  File Url
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedDocs.map((doc, index) => (
              <TableRow
                key={index}
                style={{ backgroundColor: "#0d1b2a", color: "#00f5d4" }}
                onClick={() => handleRowClick(doc)}
              >
                <TableCell>{doc.file_name}</TableCell>
                <TableCell>{getStatusChip(doc.status)}</TableCell>
                <TableCell>{doc.doc_id}</TableCell>
                <TableCell>{doc.file_url}</TableCell>
              </TableRow>
            ))}
            {displayedDocs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No matching records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredDocs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
      />
       {/* Modal for document preview */}
       <Modal open={openModal} onClose={handleCloseModal}>
       <Box
          sx={{
            position: "absolute",
            top: "75%",
            left: "75%",
            transform: "translate(-75%, -75%)",
            width: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4
          }}
        >
          <div className="App">
            <DocumentInfo selectedDoc={selectedDoc} />
          </div>
        </Box>
      </Modal>
    </Paper>
  );
};

export default DocumentStatusTable;
